import { call, put, select, takeLatest } from 'redux-saga/effects'
import { throwPcrException } from '../../pcr/store/pcr.actions'
import { getSceneDescriptionSelector } from '../selectors'
import { getServiceIdSelector } from '../../user/selectors'
import { getPcrIdSelector } from '../../home/selectors'
import { IPcr } from '../../pcr/store/pcr.types'
import { PcrDocsService } from '../../../services/pcrDocs.service'
import { ISceneDescriptionAction, ISceneDescriptionState } from '../store/sceneDescription.types'
import { SceneDescriptionConstants } from '../store/sceneDescription.constants'
import { performWithMutexHOG } from '../../../services/mutexUtility.service'
import { otherAgencyIsNonEmpty } from '../utils/sceneDescription.util'
import { getCadImportSelector } from '../../cadImport/selectors'
import { removeImportedCadDataPoint } from '../../cadImport/store/cadImport.actions'

export function* saveSceneDescription(action: ISceneDescriptionAction) {
  const sceneDescription: ISceneDescriptionState = yield select(getSceneDescriptionSelector)
  const { otherAgencies } = sceneDescription
  const serviceId: string = yield select(getServiceIdSelector)
  const pcrID: string = yield select(getPcrIdSelector)
  if (!pcrID) {
    return
  }

  const isDeletingCadComments = action?.type === SceneDescriptionConstants.DELETE_CAD_COMMENTS
  const updatedOtherAgencies = otherAgencies.filter((otherAgency) => otherAgencyIsNonEmpty(otherAgency))

  const sceneDescriptionForPcr: ISceneDescriptionState = {
    ...sceneDescription,
    otherAgencies: updatedOtherAgencies,
  }
  if (!sceneDescriptionForPcr.cadComments) {
    delete (sceneDescriptionForPcr as Partial<ISceneDescriptionState>).cadComments
  }

  const pcrDocsService = new PcrDocsService(serviceId)
  const pcrDoc: IPcr = yield call([pcrDocsService, pcrDocsService.getPCR], pcrID)
  const pcrDocUpdated: IPcr = {
    ...pcrDoc,
    sceneDescription: sceneDescriptionForPcr,
  }

  if (isDeletingCadComments) {
    const cadImport = yield select(getCadImportSelector)
    pcrDocUpdated.cadImport = {
      ...cadImport,
      importedCadData: cadImport.importedCadData.filter((dataPoint: any) => dataPoint.id !== 'COMMENTS'),
    }
  }

  try {
    yield call([pcrDocsService, pcrDocsService.savePCR], pcrDocUpdated)
    if (isDeletingCadComments) {
      yield put(removeImportedCadDataPoint('COMMENTS'))
    }
  } catch (e) {
    yield put(throwPcrException(`saveSceneDescription() error: ${e}`))
  }
}

export function* saveSceneDescriptionSaga() {
  yield takeLatest(
    [
      SceneDescriptionConstants.SET_SCENE_DESCRIPTION,
      SceneDescriptionConstants.SET_NUM_PTS_SCENE,
      SceneDescriptionConstants.SET_FIRST_ON_SCENE,
      SceneDescriptionConstants.SET_FIRST_ON_SCENE_PNNV,
      SceneDescriptionConstants.SET_LEVEL_CARE_PER_PROTOCOL,
      SceneDescriptionConstants.SET_LEVEL_CARE_PER_PROTOCOL_PNNV,
      SceneDescriptionConstants.SET_PR_OTHER_AGENCIES,
      SceneDescriptionConstants.SET_TRANSFER_OF_CARE,
      SceneDescriptionConstants.SET_TRANSFER_OF_CARE_PNNV,
      SceneDescriptionConstants.SET_LAWNUM,
      SceneDescriptionConstants.SET_FIRENUM,
      SceneDescriptionConstants.SET_PR_DISASTER,
      SceneDescriptionConstants.SET_PR_DISPO_INSTRUCTIONS,
      SceneDescriptionConstants.SET_BELONGINGS,
      SceneDescriptionConstants.SET_PR_PT_MOVED_VIA,
      SceneDescriptionConstants.SET_PR_PT_POSITION,
      SceneDescriptionConstants.SET_PR_PT_MOVED_FROM,
      SceneDescriptionConstants.SET_PR_TRANSPORT_ASSESS,
      SceneDescriptionConstants.SET_STRETCHER_PURPOSE_DESCR,
      SceneDescriptionConstants.ADD_NEW_AGENCY,
      SceneDescriptionConstants.REMOVE_AGENCY,
      SceneDescriptionConstants.SET_OTHER_AGENCY_NUMBER,
      SceneDescriptionConstants.SET_OTHER_AGENCY_TYPE,
      SceneDescriptionConstants.SET_OTHER_AGENCY_NAME,
      SceneDescriptionConstants.SET_OTHER_AGENCY_FIRST_ON_SCENE,
      SceneDescriptionConstants.SET_OTHER_AGENCY_FIRST_ON_SCENE_PNNV,
      SceneDescriptionConstants.SET_CAD_COMMENTS,
      SceneDescriptionConstants.DELETE_CAD_COMMENTS,
      SceneDescriptionConstants.SAVE_SCENE_DESCRIPTION_STATE,
    ],
    performWithMutexHOG,
    saveSceneDescription
  )
}
